import * as React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { Button, Icon } from "@blueprintjs/core";
import {
  shift,
  autoUpdate,
  flip,
  offset,
  useFloating,
  useInteractions,
  useClick,
  useDismiss,
  useRole,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
} from "@floating-ui/react";
import { ITag } from "types/Tag";
import { sortTagsAlphabetically } from "utils/tags";
import { useTags } from "hooks/useTags";
import { pickRandomAvailableTagColor, TAG_COLORS } from "./tagEditorMachine";
import { useTagMachine, useTagMachineSelector } from "./TagEditorContext";
import { DeleteTagConfirmation } from "./DeleteTagConfirmation";
import { withStopPropagation } from "utils/";

const StyledDeleteButton = styled.button`
  border: none;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 25px;
  width: 25px;

  border-radius: 4px;

  opacity: 0;
  transition: opacity 100ms ease;

  &:hover {
    background-color: var(--neutral-6);
  }

  &:hover svg {
    fill: var(--danger);
  }
`;

interface TagRowProps {
  tag: ITag;
  isEditMode: boolean;
  isSelected: boolean;
  pendingUpdates?: { label?: string; color?: string };
  isHighlighted?: boolean;
  onToggle?: () => void;
  onLabelChange?: (label: string) => void;
  onColorChange?: (color: string) => void;
  onDelete?: () => void;
  onEnter?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  inputRef?: (ref: HTMLInputElement | null) => void;
}

const TagRow = React.forwardRef<HTMLDivElement, TagRowProps>(function TagRow(
  {
    tag,
    isEditMode,
    isSelected,
    pendingUpdates,
    isHighlighted = false,
    onToggle,
    onLabelChange,
    onColorChange,
    onDelete,
    onEnter,
    autoFocus,
    disabled = false,
    inputRef: inputRefCallback,
  },
  ref
) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (inputRefCallback && inputRef.current) {
      inputRefCallback(inputRef.current);
    }
  }, [inputRefCallback]);
  const displayLabel =
    pendingUpdates?.label !== undefined ? pendingUpdates.label : tag.label;
  const displayColor =
    pendingUpdates?.color !== undefined ? pendingUpdates.color : tag.color;

  React.useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  if (isEditMode) {
    return (
      <div
        ref={ref}
        css={css`
          display: flex;
          align-items: center;

          gap: 0.25rem;

          &:hover ${StyledDeleteButton} {
            opacity: 1;
          }
        `}
      >
        <div
          css={css`
            flex: 1;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;

            ${isHighlighted &&
            css`
              background-color: var(--neutral-6);
            `}

            &:hover {
              background-color: var(--neutral-6);
            }

            &:hover [data-id="edit-button"] svg {
              fill: var(--neutral-3);
            }
          `}
        >
          <div
            data-id="edit-button"
            onClick={() => inputRef.current?.focus()}
            css={css`
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <Icon icon="edit" size={13} color="var(--neutral-4)" />
          </div>
          <ColorPickerPopover
            displayColor={displayColor}
            onColorChange={onColorChange!}
          />
          <input
            ref={inputRef}
            css={css`
              height: 17px;
              border: none;
              background-color: transparent;
              color: var(--neutral-3);
              font-size: 14px;
              font-weight: 500;
            `}
            value={displayLabel}
            onChange={(e) => onLabelChange!(e.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !(event.nativeEvent as KeyboardEvent).isComposing
              ) {
                event.preventDefault();
                event.stopPropagation();
                onEnter?.();
              }
            }}
          />
        </div>
        {onDelete && (
          <StyledDeleteButton onClick={onDelete}>
            <Icon icon="trash" size={13} color="var(--neutral-4)" />
          </StyledDeleteButton>
        )}
      </div>
    );
  }

  return (
    <div key={tag.id} ref={ref}>
      <label
        css={css`
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;

          ${isHighlighted &&
          css`
            background-color: var(--neutral-6);
          `}

          &:hover {
            background-color: var(--neutral-6);
          }
        `}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          disabled={disabled}
        />
        <div
          css={css`
            width: 13px;
            height: 13px;
            background-color: ${displayColor};
            border-radius: 9999px;
          `}
        />
        <span
          css={css`
            font-size: 14px;
            font-weight: 500;
            color: var(--neutral-3);
          `}
        >
          {displayLabel}
        </span>
      </label>
    </div>
  );
});

interface ColorPickerPopoverProps {
  displayColor: string;
  onColorChange: (color: string) => void;
}

function ColorPickerPopover({
  displayColor,
  onColorChange,
}: ColorPickerPopoverProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const colorCircleRef = React.useRef<HTMLDivElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset({
        mainAxis: 6,
        crossAxis: -9,
      }),
      flip({
        rootBoundary: "viewport",
        padding: 4,
      }),
      shift({
        rootBoundary: "viewport",
        padding: 4,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: "right-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePress: true,
    escapeKey: true,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  React.useEffect(() => {
    if (colorCircleRef.current) {
      refs.setReference(colorCircleRef.current);
    }
  }, [refs]);

  return (
    <>
      <div
        ref={colorCircleRef}
        css={css`
          width: 13px;
          height: 13px;
          background-color: ${displayColor};
          border-radius: 9999px;
          flex-shrink: 0;
          cursor: pointer;
          transition: transform 150ms ease;
          opacity: 0.7;

          &:hover {
            opacity: 1;
          }
        `}
        {...getReferenceProps()}
      />
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            css={css`
              z-index: 2;
            `}
            {...getFloatingProps()}
            onClick={withStopPropagation()}
          >
            <div
              css={css`
                padding: 0.5rem;
                display: grid;
                grid-template-columns: repeat(6, 1fr);
                gap: 0.5rem;
                background-color: var(--neutral-7);
                border-radius: 4px;
                border: 1px solid rgba(255, 255, 255, 0.12);
              `}
            >
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={withStopPropagation(() => {
                    onColorChange(color);
                    setIsOpen(false);
                  })}
                  css={css`
                    width: 20px;
                    height: 20px;
                    border-radius: 9999px;
                    background-color: ${color === displayColor
                      ? `${color}FF`
                      : `${color}30`};
                    border: 2px solid ${color}FF;
                    cursor: pointer;
                    padding: 0;
                    transition: transform 150ms ease;

                    color: var(--neutral-7);

                    &:hover {
                      background-color: ${color}FF;
                      border: 2px solid ${color}FF;
                    }
                  `}
                  aria-label={`Select color ${color}`}
                >
                  {color === displayColor && <Icon icon="tick" size={14} />}
                </button>
              ))}
            </div>
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
}

function EmptyState() {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        padding: 0.25rem;
      `}
    >
      <p
        css={css`
          color: var(--neutral-4);
          font-size: 14px;
          margin: 0;
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        `}
      >
        <span>No tags available for this organization.</span>
        <span
          css={css`
            color: var(--neutral-3);
          `}
        >
          Create the first one now.
        </span>
      </p>
    </div>
  );
}

interface TagEditorFooterProps {
  mode: "view" | "edit" | "delete-confirm" | "empty-state";
  onEditClick?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  onDeleteConfirm?: () => void;
  isSaving?: boolean;
  isDirty?: boolean;
  hasNoTags: boolean;
  emptyStateInput?: string;
  emptyStateColor?: string;
  onEmptyStateInputChange?: (value: string) => void;
  onEmptyStateColorChange?: () => void;
  onEmptyStateAdd?: () => void;
}

interface TagListContentProps {
  isEditMode: boolean;
  allTags: ITag[];
  filteredTags: ITag[];
  selectedTagIds: number[];
  pendingDeletes: Set<number>;
  pendingUpdates: Map<number, { label?: string; color?: string }>;
  pendingCreates: Array<{ tempId: string; label: string; color: string }>;
  isTogglingTag?: boolean;
  deleteConfirmation: {
    tagId: number;
    tagLabel: string;
    surveyCount: number;
  } | null;
  highlightedIndex: number | null;
  tagInputRefs: React.MutableRefObject<Map<number | string, HTMLInputElement>>;
  itemRefs: React.MutableRefObject<Map<number, HTMLElement>>;
  onTagToggle: (tag: ITag) => void;
  onLabelChange: (
    id: string | number,
    newLabel: string,
    isNew: boolean
  ) => void;
  onColorChange: (
    id: string | number,
    newColor: string,
    isNew: boolean
  ) => void;
  onDeleteClick: (
    id: string | number,
    isNew: boolean,
    catalogMeta?: { tagLabel: string; surveyCount: number }
  ) => void;
  canDelete: boolean;
  onAddNew: () => void;
  onNewTagEnter?: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}

function TagListContent({
  isEditMode,
  allTags,
  filteredTags,
  selectedTagIds,
  pendingDeletes,
  pendingUpdates,
  pendingCreates,
  isTogglingTag = false,
  deleteConfirmation,
  highlightedIndex,
  tagInputRefs,
  itemRefs,
  onTagToggle,
  onLabelChange,
  onColorChange,
  onDeleteClick,
  canDelete,
  onAddNew,
  onNewTagEnter,
  onDeleteConfirm,
  onDeleteCancel,
}: TagListContentProps) {
  if (isEditMode) {
    if (deleteConfirmation) {
      return (
        <DeleteTagConfirmation
          tagLabel={deleteConfirmation.tagLabel}
          surveyCount={deleteConfirmation.surveyCount}
          onCancel={onDeleteCancel}
          onConfirm={onDeleteConfirm}
        />
      );
    }

    return (
      <>
        <Button
          ref={(el) => {
            if (el) {
              itemRefs.current.set(0, el);
            } else {
              itemRefs.current.delete(0);
            }
          }}
          variant="minimal"
          icon="tag-add"
          size="small"
          onClick={onAddNew}
          css={css`
            justify-content: flex-start;
            padding: 0.25rem 0.5rem;
            color: var(--neutral-4);
            ${highlightedIndex === 0 &&
            css`
              background-color: var(--neutral-6) !important;
            `}
            &:hover {
              background-color: var(--neutral-6);
            }
          `}
        >
          Create new tag
        </Button>
        {filteredTags
          .filter((tag) => !pendingDeletes.has(tag.id))
          .map((tag, index) => {
            const itemIndex = index + 1;
            return (
              <TagRow
                key={tag.id}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(itemIndex, el);
                  } else {
                    itemRefs.current.delete(itemIndex);
                  }
                }}
                tag={tag}
                isEditMode={true}
                isSelected={selectedTagIds.includes(tag.id)}
                pendingUpdates={pendingUpdates.get(tag.id)}
                isHighlighted={highlightedIndex === itemIndex}
                inputRef={(ref) => {
                  if (ref) {
                    tagInputRefs.current.set(tag.id, ref);
                  } else {
                    tagInputRefs.current.delete(tag.id);
                  }
                }}
                onLabelChange={(newLabel) =>
                  onLabelChange(tag.id, newLabel, false)
                }
                onColorChange={(newColor) =>
                  onColorChange(tag.id, newColor, false)
                }
                onDelete={
                  canDelete
                    ? () =>
                        onDeleteClick(tag.id, false, {
                          tagLabel: tag.label,
                          surveyCount: tag.survey_count ?? 0,
                        })
                    : undefined
                }
              />
            );
          })}
        {pendingCreates.map((create, index) => {
          const visibleTagsCount = filteredTags.filter(
            (tag) => !pendingDeletes.has(tag.id)
          ).length;
          const createIndex = 1 + visibleTagsCount + index;

          return (
            <TagRow
              key={create.tempId}
              ref={(el) => {
                if (el) {
                  itemRefs.current.set(createIndex, el);
                } else {
                  itemRefs.current.delete(createIndex);
                }
              }}
              tag={{
                id: -1,
                label: create.label,
                color: create.color,
                organization_id: 0,
                created_by: 0,
                created_at: new Date(),
                modified_at: new Date(),
              }}
              isEditMode={true}
              isSelected={false}
              pendingUpdates={{
                label: create.label,
                color: create.color,
              }}
              isHighlighted={highlightedIndex === createIndex}
              inputRef={(ref) => {
                if (ref) {
                  tagInputRefs.current.set(create.tempId, ref);
                } else {
                  tagInputRefs.current.delete(create.tempId);
                }
              }}
              onLabelChange={(newLabel) =>
                onLabelChange(create.tempId, newLabel, true)
              }
              onColorChange={(newColor) =>
                onColorChange(create.tempId, newColor, true)
              }
              onDelete={() => onDeleteClick(create.tempId, true)}
              autoFocus={index === pendingCreates.length - 1}
              onEnter={onNewTagEnter}
            />
          );
        })}
      </>
    );
  }

  if (allTags.length === 0) {
    return <EmptyState />;
  }

  if (filteredTags.length === 0) {
    return (
      <div
        css={css`
          padding: 1rem 0.5rem;
          text-align: center;
          color: var(--neutral-4);
          font-size: 14px;
        `}
      >
        No tags matching filter
      </div>
    );
  }

  return (
    <>
      {filteredTags.map((tag, index) => (
        <TagRow
          key={tag.id}
          ref={(el) => {
            if (el) {
              itemRefs.current.set(index, el);
            } else {
              itemRefs.current.delete(index);
            }
          }}
          tag={tag}
          isEditMode={false}
          isSelected={selectedTagIds.includes(tag.id)}
          isHighlighted={highlightedIndex === index}
          onToggle={() => onTagToggle(tag)}
          disabled={isTogglingTag}
        />
      ))}
    </>
  );
}

function TagEditorFooter({
  mode,
  onEditClick,
  onCancel,
  onSave,
  onDeleteConfirm,
  isSaving = false,
  isDirty = false,
  hasNoTags,
  emptyStateInput = "",
  emptyStateColor,
  onEmptyStateInputChange,
  onEmptyStateColorChange,
  onEmptyStateAdd,
}: TagEditorFooterProps) {
  if (mode === "edit" || mode === "delete-confirm") {
    return (
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
          gap: 0.5rem;
        `}
      >
        <Button variant="minimal" icon="chevron-left" onClick={onCancel}>
          {mode === "delete-confirm" || isDirty ? "Cancel" : "Back"}
        </Button>
        {mode === "delete-confirm" ? (
          <Button intent="danger" onClick={onDeleteConfirm}>
            Delete
          </Button>
        ) : (
          <Button
            intent="primary"
            icon="tick"
            onClick={onSave}
            loading={isSaving}
            disabled={!isDirty}
          >
            Save changes
          </Button>
        )}
      </div>
    );
  }

  if (mode === "view" && !hasNoTags) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          padding: 0.5rem 0.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        `}
      >
        <Button variant="minimal" fill onClick={onEditClick}>
          Edit tags
        </Button>
      </div>
    );
  }

  if (mode === "empty-state") {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 1rem;
          width: 100%;
          padding: 0.625rem 2rem;
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        `}
      >
        <button
          css={css`
            width: 24px;
            height: 24px;
            background-color: ${emptyStateColor}30;
            border-radius: 9999px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 1;
            border: 1px solid ${emptyStateColor};
            color: ${emptyStateColor};

            @keyframes rotate {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }

            &:hover {
              background-color: ${emptyStateColor}FF;
              color: var(--neutral-7);
            }

            &:hover svg {
              animation: rotate 1500ms linear infinite;
              transform-origin: center;
            }
          `}
          onClick={onEmptyStateColorChange}
        >
          <Icon icon="refresh" size={14} />
        </button>
        <input
          type="text"
          placeholder="Tag label"
          value={emptyStateInput}
          onChange={(e) => onEmptyStateInputChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && emptyStateInput.trim()) {
              onEmptyStateAdd?.();
            }
          }}
          css={css`
            flex: 1;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            border: 1px solid rgba(255, 255, 255, 0.12);
            background-color: var(--neutral-6-translucent);
            color: var(--neutral-3);
            font-size: 14px;

            &:focus {
              border-color: var(--neutral-4);
              outline: none;
            }
          `}
        />
        <Button
          icon="chevron-right"
          intent="success"
          size="small"
          onClick={onEmptyStateAdd}
          disabled={!emptyStateInput.trim()}
        />
      </div>
    );
  }

  return null;
}

export function TagEditor() {
  const actorRef = useTagMachine();
  const { tags: allTagsRaw } = useTags();
  const allTags = React.useMemo(
    () => sortTagsAlphabetically([...allTagsRaw]),
    [allTagsRaw]
  );
  const surveyTagIds = useTagMachineSelector((s) => s.context.surveyTagIds);
  const filterQuery = useTagMachineSelector((s) => s.context.filterQuery);
  const pendingUpdates = useTagMachineSelector((s) => s.context.pendingUpdates);
  const pendingCreates = useTagMachineSelector((s) => s.context.pendingCreates);
  const pendingDeletes = useTagMachineSelector((s) => s.context.pendingDeletes);
  const deleteConfirmation = useTagMachineSelector(
    (s) => s.context.deleteConfirmation
  );
  const emptyStateInput = useTagMachineSelector(
    (s) => s.context.emptyStateInput
  );
  const emptyStateColor = useTagMachineSelector(
    (s) => s.context.emptyStateColor
  );
  const highlightedTagIndex = useTagMachineSelector(
    (s) => s.context.highlightedTagIndex
  );
  const canDelete = useTagMachineSelector((s) => s.context.canDelete);
  const isOpen = useTagMachineSelector((s) => s.matches({ ready: "open" }));
  const isEditMode = useTagMachineSelector(
    (s) =>
      s.matches({ ready: { open: "editing" } }) ||
      s.matches({ ready: { open: "confirmingDelete" } }) ||
      s.matches({ ready: { open: "deleting" } }) ||
      s.matches({ ready: { open: "saving" } })
  );
  const isSaving = useTagMachineSelector((s) =>
    s.matches({ ready: { open: "saving" } })
  );
  const isTogglingTag = useTagMachineSelector((s) =>
    s.matches({ ready: { open: "togglingTag" } })
  );
  const isDirty = useTagMachineSelector(
    (s) =>
      s.context.pendingUpdates.size > 0 || s.context.pendingCreates.length > 0
  );

  const tagListScrollRef = React.useRef<HTMLDivElement>(null);
  const tagFilterInputRef = React.useRef<HTMLInputElement>(null);
  const tagInputRefs = React.useRef<Map<number | string, HTMLInputElement>>(
    new Map()
  );
  const itemRefs = React.useRef<Map<number, HTMLElement>>(new Map());

  const filteredTags = React.useMemo(() => {
    let tags = allTags;
    if (filterQuery.trim()) {
      tags = allTags.filter((tag) =>
        tag.label.toLowerCase().includes(filterQuery.toLowerCase())
      );
    }
    return sortTagsAlphabetically(tags);
  }, [allTags, filterQuery]);

  const selectedTags = React.useMemo(
    () => allTags.filter((tag) => surveyTagIds.includes(tag.id)),
    [allTags, surveyTagIds]
  );

  const itemCount = React.useMemo(() => {
    if (isEditMode) {
      const visibleTags = filteredTags.filter(
        (tag) => !pendingDeletes.has(tag.id)
      );
      return 1 + visibleTags.length + pendingCreates.length;
    }
    return filteredTags.length;
  }, [isEditMode, filteredTags, pendingDeletes, pendingCreates]);

  const getHighlightedItem = React.useCallback(() => {
    if (highlightedTagIndex === null) return null;
    if (isEditMode) {
      if (highlightedTagIndex === 0) {
        return { type: "addNew" as const, index: 0 };
      }
      const visibleTags = filteredTags.filter(
        (tag) => !pendingDeletes.has(tag.id)
      );
      const tagIndex = highlightedTagIndex - 1;
      if (tagIndex < visibleTags.length) {
        return {
          type: "tag" as const,
          tag: visibleTags[tagIndex],
          index: highlightedTagIndex,
        };
      } else {
        const createIndex = tagIndex - visibleTags.length;
        if (createIndex < pendingCreates.length) {
          return {
            type: "create" as const,
            create: pendingCreates[createIndex],
            index: highlightedTagIndex,
          };
        }
      }
    } else {
      if (highlightedTagIndex < filteredTags.length) {
        return { type: "tag" as const, tag: filteredTags[highlightedTagIndex] };
      }
    }
    return null;
  }, [
    highlightedTagIndex,
    isEditMode,
    filteredTags,
    pendingDeletes,
    pendingCreates,
  ]);

  const handleFilterKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        actorRef.send({ type: "HIGHLIGHT_NEXT", itemCount });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        actorRef.send({ type: "HIGHLIGHT_PREV", itemCount });
      } else if (e.key === "Enter") {
        e.preventDefault();
        const highlighted = getHighlightedItem();
        if (highlighted) {
          if (isEditMode) {
            if (highlighted.type === "addNew") {
              actorRef.send({
                type: "ADD_NEW",
                color: pickRandomAvailableTagColor(
                  allTags,
                  pendingCreates,
                  pendingDeletes,
                  pendingUpdates
                ),
              });
            } else if (highlighted.type === "tag") {
              const inputRef = tagInputRefs.current.get(highlighted.tag.id);
              if (inputRef) {
                setTimeout(() => inputRef.focus(), 0);
              }
            } else if (highlighted.type === "create") {
              const inputRef = tagInputRefs.current.get(
                highlighted.create.tempId
              );
              if (inputRef) {
                setTimeout(() => inputRef.focus(), 0);
              }
            }
          } else {
            if (highlighted.type === "tag") {
              actorRef.send({ type: "TOGGLE_TAG", tag: highlighted.tag });
            }
          }
        }
      }
    },
    [
      itemCount,
      getHighlightedItem,
      isEditMode,
      actorRef,
      allTags,
      pendingCreates,
      pendingDeletes,
      pendingUpdates,
    ]
  );

  const prevIsEditMode = React.useRef<boolean>(isEditMode);

  React.useEffect(() => {
    if (!prevIsEditMode.current && isEditMode && tagListScrollRef.current) {
      tagListScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    prevIsEditMode.current = isEditMode;
  }, [isEditMode]);

  React.useEffect(() => {
    if (highlightedTagIndex !== null && tagListScrollRef.current) {
      const highlightedElement = itemRefs.current.get(highlightedTagIndex);
      const scrollContainer = tagListScrollRef.current;

      if (highlightedElement && scrollContainer) {
        const isFirstItem = highlightedTagIndex === 0;
        const isLastItem = highlightedTagIndex === itemCount - 1;

        if (isFirstItem) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else if (isLastItem) {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight - scrollContainer.clientHeight,
            behavior: "smooth",
          });
        } else {
          highlightedElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    }
  }, [highlightedTagIndex, itemCount]);

  React.useEffect(() => {
    if (tagFilterInputRef.current) {
      tagFilterInputRef.current.focus();
    }
  }, [isOpen, isEditMode]);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      e.preventDefault();
      e.stopPropagation();

      const isInDeleteConfirmMode = isEditMode && deleteConfirmation !== null;
      const isInEditMode = isEditMode && deleteConfirmation === null;
      const isInEmptyStateMode = !isEditMode && allTags.length === 0;
      const isInViewMode = !isEditMode && allTags.length > 0;

      if (isInViewMode || isInEmptyStateMode) {
        actorRef.send({ type: "CLOSE" });
      } else if (isInDeleteConfirmMode) {
        actorRef.send({ type: "CANCEL_DELETE" });
      } else if (isInEditMode) {
        actorRef.send({ type: "CANCEL" });
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isEditMode, deleteConfirmation, allTags.length, actorRef]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        actorRef.send({ type: "CLOSE" });
      } else {
        actorRef.send({ type: "OPEN" });
      }
    },
    [isEditMode, isDirty, actorRef]
  );

  const sendEmptyStateAdd = React.useCallback(() => {
    actorRef.send({
      type: "EMPTY_STATE_ADD",
      nextEmptyStateColor: pickRandomAvailableTagColor(
        allTags,
        [
          {
            tempId: "__empty_pending__",
            label: emptyStateInput,
            color: emptyStateColor,
          },
        ],
        pendingDeletes,
        pendingUpdates
      ),
    });
  }, [
    actorRef,
    allTags,
    emptyStateInput,
    emptyStateColor,
    pendingDeletes,
    pendingUpdates,
  ]);

  const conditionalCrossAxisOffset = {
    name: "conditionalCrossAxisOffset",
    fn({
      x,
      y,
      middlewareData,
    }: {
      x: number;
      y: number;
      middlewareData: { shift?: { x: number; y: number } };
    }) {
      const shiftData = middlewareData.shift;
      const hasShifted = shiftData && shiftData.y !== 0;

      if (hasShifted) {
        return {
          x: x + 24,
          y: y,
        };
      }

      return { x, y };
    },
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    middleware: [
      offset({
        mainAxis: 0,
        crossAxis: 0,
      }),
      flip({
        rootBoundary: "viewport",
        padding: 4,
      }),
      shift({
        rootBoundary: "viewport",
        padding: 16,
        mainAxis: true,
        crossAxis: true,
      }),
      conditionalCrossAxisOffset,
    ],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { escapeKey: false });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        width: fit-content;
        padding: 8px;

        &:hover button[data-id="tag-editor-button"] {
          border-color: var(--neutral-4);
          color: var(--neutral-3);
        }

        &:hover span[data-id="add-tags-button"] {
          color: var(--neutral-3);
        }
      `}
      ref={refs.setReference}
      {...getReferenceProps({
        onClick: (event) => {
          event.stopPropagation();
        },
      })}
    >
      <button
        data-id="tag-editor-button"
        css={css`
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border: 1px solid var(--neutral-5);
          border-radius: 9999px;
          color: var(--neutral-4);
          background-color: transparent;
          transition:
            border-color 100ms ease,
            color 100ms ease;
          opacity: 1;
          cursor: pointer;

          ${isOpen &&
          css`
            border-color: var(--neutral-4);
            color: var(--neutral-3);
          `}
        `}
      >
        <Icon icon="tags" size={10} />
      </button>

      <div
        css={css`
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          flex: 1;
          min-width: 0;
          gap: 0.25rem;
        `}
      >
        {selectedTags.length === 0 && (
          <div
            css={css`
              height: 18px;
              display: flex;
              align-items: center;
            `}
          >
            <span
              data-id="add-tags-button"
              css={css`
                color: var(--neutral-4);
                font-size: 11px;
                font-weight: 700;
                transition: color 100ms ease;
              `}
            >
              ADD TAGS
            </span>
          </div>
        )}

        {isOpen && (
          <FloatingPortal>
            <FloatingOverlay
              lockScroll={false}
              className="bp6-dark"
              css={css`
                background: transparent;
                z-index: 1000;
              `}
            >
              <FloatingFocusManager context={context} modal={false}>
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  {...getFloatingProps()}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.stopPropagation();
                    }
                  }}
                >
                  <div
                    css={css`
                      background-color: var(--neutral-7);
                      border-radius: 9px;
                      border: 1px solid rgba(255, 255, 255, 0.12);
                      width: 320px;
                    `}
                  >
                    <div
                      css={css`
                        border-bottom: 1px solid rgba(255, 255, 255, 0.12);
                        padding: 0.5rem;
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                      `}
                    >
                      <h5
                        css={css`
                          color: var(--neutral-3);
                          padding-left: 0.5rem;
                        `}
                      >
                        {deleteConfirmation
                          ? "Delete tag for this organization"
                          : isEditMode
                            ? "Edit tags for this organization"
                            : "Apply tags to this survey"}
                      </h5>
                      {deleteConfirmation || allTags.length === 0 ? null : (
                        <div
                          css={css`
                            position: relative;
                          `}
                        >
                          <div
                            css={css`
                              position: absolute;
                              left: 0;
                              top: 0;
                              bottom: 0;
                              width: 32px;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                            `}
                          >
                            <Icon
                              icon="search"
                              size={12}
                              color={
                                deleteConfirmation
                                  ? "var(--neutral-4)"
                                  : "var(--neutral-3)"
                              }
                            />
                          </div>
                          <input
                            ref={tagFilterInputRef}
                            type="text"
                            placeholder="Filter tags"
                            value={filterQuery}
                            onChange={(e) =>
                              actorRef.send({
                                type: "SET_FILTER",
                                query: e.target.value,
                              })
                            }
                            onKeyDown={handleFilterKeyDown}
                            css={css`
                              width: 100%;
                              padding: 0.25rem 0.5rem 0.25rem 2rem;
                              border-radius: 4px;
                              border: 1px solid rgba(255, 255, 255, 0.12);
                              background-color: var(--neutral-6-translucent);
                              color: var(--neutral-3);
  
                              &:focus {
                                border-color: var(--neutral-4);
                              }
                            `}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                      `}
                    >
                      <div
                        ref={tagListScrollRef}
                        css={css`
                          padding: 0.5rem 0.5rem;
                          display: flex;
                          flex-direction: column;
                          gap: 0.25rem;
                          max-height: 244px;
                          overflow-y: auto;
                        `}
                      >
                        <TagListContent
                          isEditMode={isEditMode}
                          allTags={allTags}
                          filteredTags={filteredTags}
                          selectedTagIds={surveyTagIds}
                          pendingDeletes={pendingDeletes}
                          pendingUpdates={pendingUpdates}
                          pendingCreates={pendingCreates}
                          isTogglingTag={isTogglingTag}
                          deleteConfirmation={deleteConfirmation}
                          highlightedIndex={highlightedTagIndex}
                          tagInputRefs={tagInputRefs}
                          itemRefs={itemRefs}
                          onTagToggle={(tag) =>
                            actorRef.send({ type: "TOGGLE_TAG", tag })
                          }
                          onLabelChange={(id, newLabel, isNew) =>
                            actorRef.send({
                              type: "UPDATE_LABEL",
                              id,
                              label: newLabel,
                              isNew,
                            })
                          }
                          onColorChange={(id, newColor, isNew) =>
                            actorRef.send({
                              type: "UPDATE_COLOR",
                              id,
                              color: newColor,
                              isNew,
                            })
                          }
                          onDeleteClick={(id, isNew, catalogMeta) =>
                            actorRef.send({
                              type: "DELETE_TAG",
                              id,
                              isNew,
                              ...(catalogMeta
                                ? {
                                    tagLabel: catalogMeta.tagLabel,
                                    surveyCount: catalogMeta.surveyCount,
                                  }
                                : {}),
                            })
                          }
                          canDelete={canDelete}
                          onAddNew={() =>
                            actorRef.send({
                              type: "ADD_NEW",
                              color: pickRandomAvailableTagColor(
                                allTags,
                                pendingCreates,
                                pendingDeletes,
                                pendingUpdates
                              ),
                            })
                          }
                          onNewTagEnter={() => actorRef.send({ type: "SAVE" })}
                          onDeleteConfirm={() =>
                            actorRef.send({ type: "CONFIRM_DELETE" })
                          }
                          onDeleteCancel={() =>
                            actorRef.send({ type: "CANCEL_DELETE" })
                          }
                        />
                      </div>
                      <TagEditorFooter
                        mode={
                          isEditMode
                            ? deleteConfirmation
                              ? "delete-confirm"
                              : "edit"
                            : allTags.length === 0
                              ? "empty-state"
                              : "view"
                        }
                        onEditClick={() => actorRef.send({ type: "ENTER_EDIT" })}
                        onCancel={
                          deleteConfirmation
                            ? () => actorRef.send({ type: "CANCEL_DELETE" })
                            : () => actorRef.send({ type: "CANCEL" })
                        }
                        onSave={() => actorRef.send({ type: "SAVE" })}
                        onDeleteConfirm={() =>
                          actorRef.send({ type: "CONFIRM_DELETE" })
                        }
                        isSaving={isSaving}
                        isDirty={isDirty}
                        hasNoTags={allTags.length === 0}
                        emptyStateInput={emptyStateInput}
                        emptyStateColor={emptyStateColor}
                        onEmptyStateInputChange={(value) =>
                          actorRef.send({ type: "SET_EMPTY_STATE_INPUT", value })
                        }
                        onEmptyStateColorChange={() =>
                          actorRef.send({
                            type: "SET_EMPTY_STATE_COLOR",
                            color: pickRandomAvailableTagColor(
                              allTags,
                              [],
                              pendingDeletes,
                              pendingUpdates
                            ),
                          })
                        }
                        onEmptyStateAdd={sendEmptyStateAdd}
                      />
                    </div>
                  </div>
                </div>
              </FloatingFocusManager>
            </FloatingOverlay>
          </FloatingPortal>
        )}

        {selectedTags.map((tag) => (
          <div
            key={tag.id}
            css={css`
              border: 1px solid ${tag.color};
              background-color: ${tag.color}30;
              border-radius: 9px;
              padding: 0.125rem 0.725rem;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 18px;
            `}
          >
            <span
              css={css`
                font-size: 11px;
                font-weight: 700;
                color: ${tag.color};
                text-transform: uppercase;
              `}
            >
              {tag.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
